import type {
  HotelMapFullscreenCopy,
  HotelOfferPagesNoticeCopy,
  HotelResultsPageCopy,
  HotelResultsPageProps,
  HotelOfferView,
  HotelSearchAreaNoticeCopy,
} from '@voll-intelligence/intelligence-ui-kit';

import { genUiI18n, type GenUiTranslate } from '../../../shared/ui/i18n.js';
import { type HotelPlannerBlock, placeLocality } from '@voll-intelligence/genui-contract';
import { hotelDateRangeLabel } from './hotel_planner_view.js';

/* Os textos da página de resultados de hotel, puros e por idioma. A página é a
   mesma na conversa ("Ver todos os hotéis") e na bancada do web-chat, então os
   dois hosts leem daqui (o `xCopy(locale)` do padrão B+, #4996). */

/** O nome do lugar pesquisado, na régua do card ("Escritório São Paulo"). */
export function hotelPlaceName(ui: HotelPlannerBlock): string | undefined {
  return ui.place ?? ui.placeDetails?.name ?? ui.placeDetails?.city;
}

/** O título de escolha — "Hotéis em {lugar} · {9–11 set}", paridade com o
 *  protótipo (`tituloDaEscolha`): sem lugar cai no genérico; sem as duas datas
 *  o título para no lugar. */
function pageTitle(
  ui: HotelPlannerBlock,
  dates: { checkin?: string; checkout?: string },
  t: GenUiTranslate,
  locale: string,
): string {
  const place = hotelPlaceName(ui);
  if (!place) return t('hotel.results.title');
  const range = hotelDateRangeLabel(dates, locale);
  const title = placeTitle(ui, place, t);
  return range ? `${title} · ${range}` : title;
}

/** O lugar do título. A área do mapa (#3114) não é um lugar com nome: o título
 *  diz a área, não a coordenada nem o rótulo sintético do pedido. Lugar que
 *  não é cidade ("AVENIDA PAULISTA") leva a cidade resolvida ao lado — sem ela
 *  um homônimo de outra cidade só se revelava no mapa (QA de 29/09, A1). */
function placeTitle(ui: HotelPlannerBlock, place: string, t: GenUiTranslate): string {
  if (ui.placeSource === 'map_area') return t('hotel.results.pageTitleMapArea');
  const locality = ui.placeDetails ? placeLocality(ui.placeDetails, place) : undefined;
  return locality
    ? t('hotel.results.pageTitleNear', { place, locality })
    : t('hotel.results.pageTitle', { place });
}

/** O contexto que segue a contagem na meta — "{n} noites", o multiplicador do
 *  preço por noite dos cards. O lugar fica só no título (e na pílula do host):
 *  repetido aqui, o resumo não cabia ao lado dos controles no estreito (#5015). */
function pageContext(ui: HotelPlannerBlock, t: GenUiTranslate): string | undefined {
  if (ui.nights === undefined) return undefined;
  return t(ui.nights === 1 ? 'hotel.results.pageContext_one' : 'hotel.results.pageContext_other', {
    count: ui.nights,
  });
}

/** A copy inteira da página no vocabulário do kit — título, contexto,
 *  contagens e ordenador saem das MESMAS chaves do painel em overlay. */
function pageCopy(
  ui: HotelPlannerBlock,
  dates: { checkin?: string; checkout?: string },
  t: GenUiTranslate,
  locale: string,
  currency: string,
): HotelResultsPageCopy {
  const context = pageContext(ui, t);
  // Inteiros: o chip diz a faixa em duas palavras, não a diária com centavos.
  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
  return {
    title: pageTitle(ui, dates, t, locale),
    ...(context ? { context } : {}),
    totalHotelsLabel: (total) =>
      t(total === 1 ? 'hotel.results.total_one' : 'hotel.results.total_other', { count: total }),
    visibleHotelsLabel: (visible, total) =>
      t(visible === 1 ? 'hotel.results.visibleOfTotal_one' : 'hotel.results.visibleOfTotal_other', {
        visible,
        total,
      }),
    shareLabel: t('hotel.results.share'),
    showMapLabel: t('hotel.results.showMap'),
    mapPreviewCloseLabel: t('hotel.map.previewClose'),
    filteredEmpty: {
      title: t('hotel.results.filteredEmpty'),
      clearLabel: t('hotel.results.clearFilters'),
    },
    sort: {
      sortLabel: t('hotel.results.sort'),
      orderLabel: (order) => t(`hotel.results.sort.${order}`),
    },
    searchArea: {
      searchLabel: t('hotel.results.searchThisArea'),
      searchingLabel: t('hotel.results.searchingThisArea'),
    },
    chips: {
      toolbarLabel: t('hotel.results.chips.toolbar'),
      backLabel: t('hotel.results.back'),
      priceUpTo: (maximum) => t('hotel.results.chips.priceUpTo', { price: money.format(maximum) }),
      priceFrom: (minimum) => t('hotel.results.chips.priceFrom', { price: money.format(minimum) }),
      priceBetween: (minimum, maximum) =>
        t('hotel.results.chips.priceBetween', {
          min: money.format(minimum),
          max: money.format(maximum),
        }),
    },
  };
}

/** O compartilhar da busca na página (#5156): o link sempre; com `onDownloadPdf`,
 *  o menu com a cotação em PDF dos hotéis em cena (ordem e cortes da tela). Na
 *  espera da 1ª busca não há lista: `pdfDisabled` deixa o item no menu, apagado. */
export type HotelPageShare = {
  readonly onCopyLink: () => void;
  readonly onDownloadPdf?: (visible: readonly HotelOfferView[]) => void;
  readonly pdfDisabled?: boolean;
};

function pageShareProps(
  share: HotelPageShare | undefined,
  t: GenUiTranslate,
): Pick<HotelResultsPageProps, 'onShare' | 'pdfShare'> {
  if (!share) return {};
  const pdf = share.onDownloadPdf;
  return {
    onShare: share.onCopyLink,
    ...(pdf
      ? {
          pdfShare: {
            copy: {
              copyLinkLabel: t('searchShare.copyLink'),
              downloadPdfLabel: t('searchShare.downloadPdf'),
            },
            onDownloadPdf: pdf,
            ...(share.pdfDisabled ? { pdfDisabled: true } : {}),
          },
        }
      : {}),
  };
}

/** O texto da página: título da busca, contagens, ordenador, chips e o "buscar
 *  nesta área". `dates` são as do rascunho confirmado — o título diz a busca que
 *  produziu a página. */
export function hotelResultsPageCopy(input: {
  ui: HotelPlannerBlock;
  dates: { checkin?: string; checkout?: string };
  locale: string;
  currency: string;
}): HotelResultsPageCopy {
  const { t, locale } = genUiI18n(input.locale);
  return pageCopy(input.ui, input.dates, t, locale, input.currency);
}

/** O compartilhar da busca na página, no vocabulário do `HotelResultsPage`. */
export function hotelPageShareProps(
  share: HotelPageShare | undefined,
  locale: string,
): Pick<HotelResultsPageProps, 'onShare' | 'pdfShare'> {
  return pageShareProps(share, genUiI18n(locale).t);
}

/** Os avisos da lista completa (`hotelOfferPagesNotice` do kit). */
export function hotelOfferPagesNoticeCopy(locale: string): HotelOfferPagesNoticeCopy {
  const { t } = genUiI18n(locale);
  return {
    loadingAllTitle: t('hotel.results.loadingAll'),
    staleRetryingTitle: t('hotel.results.staleRetrying'),
    unavailableTitle: t('hotel.results.unavailableTitle'),
    unavailableMessage: t('hotel.results.unavailable'),
    staleTitle: t('hotel.results.staleTitle'),
    staleMessage: t('hotel.results.stale'),
    searchAgainLabel: t('hotel.results.staleAction'),
    errorTitle: t('hotelPlanner.providerErrorTitle'),
    errorMessage: t('hotelPlanner.providerError'),
    retryLabel: t('hotelPlanner.retry'),
  };
}

/** O desfecho do "buscar nesta área" (`hotelSearchAreaNotice` do kit). */
export function hotelSearchAreaNoticeCopy(locale: string): HotelSearchAreaNoticeCopy {
  const { t } = genUiI18n(locale);
  return {
    searchingTitle: t('hotel.results.searching'),
    emptyTitle: t('hotelPlanner.noResultsTitle'),
    emptyMessage: t('hotel.results.mapAreaEmpty'),
    errorTitle: t('hotelPlanner.providerErrorTitle'),
    errorMessage: t('hotelPlanner.providerError'),
    retryLabel: t('hotelPlanner.retry'),
  };
}

/** O mapa em tela cheia. */
export function hotelMapFullscreenCopy(locale: string): HotelMapFullscreenCopy {
  const { t } = genUiI18n(locale);
  return {
    backLabel: t('hotel.results.back'),
    showListLabel: t('hotel.results.showList'),
    expandFiltersLabel: t('hotel.results.expandFilters'),
    visibleHotelsLabel: (count) =>
      t(count === 1 ? 'hotel.results.visibleHotels_one' : 'hotel.results.visibleHotels_other', {
        count,
      }),
    loadMoreLabel: t('hotel.results.loadMore'),
    loadingMoreLabel: t('hotel.results.loadingMore'),
    allLoadedLabel: t('hotel.results.allLoaded'),
    searchAreaLabel: t('hotel.results.searchThisArea'),
    searchingAreaLabel: t('hotel.results.searchingThisArea'),
  };
}
